<template>
  <PayOpsModalShell
    :visible="visible"
    title="Refund & Cancel"
    subtitle="Review the refund impact and explicitly confirm the order"
    icon="fa-exclamation-triangle"
    tone="danger"
    :close-disabled="isSubmitting"
    @close="$emit('close')"
  >
    <div class="alert alert-warning outline small">
      <i class="fa fa-info-circle me-1"></i>
      This action will cancel the order and flag it as refund-eligible. The PSP refund must be
      performed manually on the provider platform. A pending refund-tracking record will be created
      automatically.
    </div>

    <div class="mb-3">
      <label class="form-label fw-bold">Reason Category <span class="text-danger">*</span></label>
      <select v-model="reasonCategory" class="form-select border border-primary">
        <option value="">Select a category...</option>
        <option value="Fraud">Fraud</option>
        <option value="Gift card">Gift card</option>
        <option value="Wrong pump">Wrong pump</option>
        <option value="Goodwill">Goodwill</option>
        <option value="Test">Test</option>
        <option value="Other">Other</option>
      </select>
    </div>

    <div class="mb-3">
      <label class="form-label fw-bold"
        >Reason <span class="text-danger">*</span> (min 10 chars)</label
      >
      <textarea
        v-model="reason"
        class="form-control border border-primary"
        rows="3"
        placeholder="Explain why this order needs refund & cancel..."
      ></textarea>
      <div class="form-text">Characters: {{ reason.length }} / 10 minimum</div>
    </div>

    <div class="mb-3">
      <label class="form-label fw-bold">
        Confirm Order UUID (last 8 characters) <span class="text-danger">*</span>
      </label>
      <div class="mb-1 font-monospace small text-muted">Full UUID: {{ orderUuid }}</div>
      <input
        v-model="uuidConfirm"
        type="text"
        class="form-control border border-primary font-monospace"
        placeholder="Enter last 8 characters..."
        maxlength="8"
      />
      <div v-if="uuidConfirm && !uuidMatches" class="text-danger small mt-1">
        Does not match the expected last 8 characters.
      </div>
    </div>

    <div v-if="hasGiftCards" class="alert alert-info outline small">
      <i class="fa fa-gift me-1"></i>
      This order may have gift card balances. Refund &amp; cancel will trigger voucher-service
      release-balance.
    </div>
    <template #footer>
      <button class="btn btn-outline-secondary" :disabled="isSubmitting" @click="$emit('close')">
        Cancel
      </button>
      <button class="btn btn-danger" :disabled="!canSubmit || isSubmitting" @click="submit">
        <span v-if="isSubmitting">
          <span class="spinner-border spinner-border-sm me-1"></span>Processing...
        </span>
        <span v-else><i class="fa fa-times-circle me-1"></i>Confirm Refund &amp; Cancel</span>
      </button>
    </template>
  </PayOpsModalShell>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import PayOpsModalShell from '../../shared/PayOpsModalShell.vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  orderUuid: { type: String, default: '' },
  hasGiftCards: { type: Boolean, default: false },
  isSubmitting: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'submit']);

const reasonCategory = ref('');
const reason = ref('');
const uuidConfirm = ref('');

const expectedLast8 = computed(() => (props.orderUuid || '').slice(-8));
const uuidMatches = computed(() => uuidConfirm.value === expectedLast8.value);
const canSubmit = computed(
  () => !!reasonCategory.value && reason.value.length >= 10 && uuidMatches.value,
);

watch(
  () => props.visible,
  (v) => {
    if (v) {
      reasonCategory.value = '';
      reason.value = '';
      uuidConfirm.value = '';
    }
  },
);

function submit() {
  if (!canSubmit.value) return;
  emit('submit', { reason: reason.value, reasonCategory: reasonCategory.value });
}
</script>
